/**
 * WhiteboardManager.tsx
 * Blank canvas whiteboard panel with save-to-slide capability.
 */

import React, { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Trash2 } from 'lucide-react';
import { usePresenterStore } from '@store/presenterStore';
import Button from '@components/shared/Button';

export default function WhiteboardManager(): React.ReactElement {
  const { setWhiteboardOpen } = usePresenterStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);

  const clearWhiteboard = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    isDrawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  }, []);

  const handleSaveToSlide = async () => {
    // Insert current whiteboard as a new blank slide + image shape
    if (!Office.context.requirements.isSetSupported('PowerPointApi', '1.2')) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    // Base64 without prefix
    const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
    await PowerPoint.run(async (ctx) => {
      ctx.presentation.slides.add();
      await ctx.sync();
      const count = ctx.presentation.slides.getCount();
      await ctx.sync();
      const newSlide = ctx.presentation.slides.getItemAt(count.value - 1);
      // addImage isn't in the current typings; available in newer API sets.
      (newSlide.shapes as unknown as { addImage: (b64: string) => void }).addImage(base64);
      await ctx.sync();
    });
  };

  return (
    <motion.div
      className="fixed inset-4 z-[9020] glass rounded-3xl overflow-hidden shadow-glass pointer-events-auto"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
    >
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-surface-border">
        <span className="text-sm font-bold text-slate-900">🖊 Whiteboard</span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" icon={<Trash2 size={13} />} onClick={clearWhiteboard}>Clear</Button>
          <Button size="sm" variant="secondary" icon={<Save size={13} />} onClick={handleSaveToSlide}>Save to Slide</Button>
          <button onClick={() => setWhiteboardOpen(false)} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-surface-elevated transition-colors">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="w-full h-[calc(100%-52px)] bg-white cursor-crosshair"
        width={1200}
        height={800}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={() => { isDrawing.current = false; }}
      />
    </motion.div>
  );
}
